import type maplibregl from 'maplibre-gl';
import type { DataSource, Entity } from '../types/entity';
import { EntityRenderer } from '../map/EntityRenderer';
import { currentMapBounds } from '../map/setupMap';

/**
 * Uma camada = uma fonte + um renderer + um intervalo de polling
 * próprio. Fontes voláteis (voos) atualizam mais rápido que fontes
 * quase-estáticas (câmeras) — cada uma declara seu próprio ritmo aqui
 * em vez de compartilhar um único timer global.
 */
const POLL_INTERVAL_MS: Record<string, number> = {
  flights: 15_000,
  earthquakes: 60_000,
  'webcams-windy': 5 * 60_000,
  'webcams-wsdot': 5 * 60_000,
  'webcams-der-sp': 60 * 60_000, // dado estático — refresh horário é suficiente
  wildfires: 5 * 60_000,
  satellites: 30_000,
  maritime: 20_000,
  news: 15 * 60_000, // lê o cache já materializado (/api/news), não a APITube direto — sem custo de quota
};

const DEFAULT_POLL_INTERVAL_MS = 30_000;

export class LayerOrchestrator {
  private readonly map: maplibregl.Map;
  private readonly onEntityClick: (entity: Entity) => void;
  private readonly timers = new Map<string, ReturnType<typeof setInterval>>();
  private readonly renderers = new Map<string, EntityRenderer>();

  constructor(map: maplibregl.Map, onEntityClick: (entity: Entity) => void) {
    this.map = map;
    this.onEntityClick = onEntityClick;
  }

  /** Ativa uma camada: busca imediatamente, depois agenda polling. */
  activate(source: DataSource): void {
    if (this.timers.has(source.layer)) return; // já ativa, no-op

    // 'news' não usa o ping de assinatura — dado com 12h de atraso no
    // free tier da APITube não deveria comunicar "ao vivo" visualmente.
    const usesPingAnimation = source.layer !== 'news';
    const renderer = new EntityRenderer(this.map, source.layer, this.onEntityClick, usesPingAnimation);
    this.renderers.set(source.layer, renderer);

    const tick = () => this.refresh(source, renderer);
    void tick();

    const interval = POLL_INTERVAL_MS[source.layer] ?? DEFAULT_POLL_INTERVAL_MS;
    this.timers.set(source.layer, setInterval(tick, interval));
  }

  deactivate(layer: string): void {
    const timer = this.timers.get(layer);
    if (timer) clearInterval(timer);
    this.timers.delete(layer);

    this.renderers.get(layer)?.clear();
    this.renderers.delete(layer);
  }

  private async refresh(source: DataSource, renderer: EntityRenderer): Promise<void> {
    try {
      const bounds = currentMapBounds(this.map);
      const entities = await source.fetchEntities(bounds);
      renderer.render(entities);
    } catch (err) {
      // Uma camada falhando não deve derrubar as outras — loga e segue.
      console.error(`[LayerOrchestrator] falha ao atualizar camada "${source.layer}"`, err);
    }
  }
}
